import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

const RewardCartContext = createContext();
const STORAGE_KEY = 'videopeel_reward_cart';

export const useRewardCart = () => {
  const context = useContext(RewardCartContext);
  if (!context) {
    throw new Error('useRewardCart must be used within RewardCartProvider');
  }
  return context;
};

export const RewardCartProvider = ({ children }) => {
  const [rewardItems, setRewardItems] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load reward cart from localStorage:', error);
      return [];
    }
  });

  const [onFirstItemAdded, setOnFirstItemAdded] = useState(null);
  const [openCartCallback, setOpenCartCallback] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rewardItems));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save reward cart to localStorage:', error);
    }
  }, [rewardItems]);

  const addToCart = useCallback((submission) => {
    if (submission?.giftOrderStatus) {
      return;
    }
    
    setRewardItems((prev) => {
      const exists = prev.find((item) => item.submissionId === submission.campaignSubmissionId);
      if (exists) {
        return prev;
      }
      const newItems = [
        ...prev,
        {
          submissionId: submission.campaignSubmissionId,
          submitterName: submission.submitterName || 'Unknown',
          submitterEmail: submission.submitterEmail || submission.email || '',
          campaignName: submission.campaignName || '',
          campaignId: submission.campaignId,
          campaignLink: submission.campaignLink || submission.campaign_link,
          campaignRewardAmount: submission.campaignRewardAmount || 0,
        },
      ];
      
      if (prev.length === 0 && onFirstItemAdded) {
        setTimeout(() => onFirstItemAdded(), 50);
      }
      
      return newItems;
    });
  }, [onFirstItemAdded]);

  const removeFromCart = useCallback((submissionId) => {
    setRewardItems((prev) => prev.filter((item) => item.submissionId !== submissionId));
  }, []);

  const isInCart = useCallback(
    (submissionId) => {
      return rewardItems.some((item) => item.submissionId === submissionId);
    },
    [rewardItems]
  );

  const clearCart = useCallback(() => {
    setRewardItems([]);
  }, []);

  const registerFirstItemCallback = useCallback((callback) => {
    setOnFirstItemAdded(() => callback);
  }, []);

  const registerOpenCartCallback = useCallback((callback) => {
    setOpenCartCallback(() => callback);
  }, []);

  const openCart = useCallback(() => {
    if (openCartCallback) {
      openCartCallback();
    }
  }, [openCartCallback]);

  const value = {
    rewardItems,
    addToCart,
    removeFromCart,
    isInCart,
    clearCart,
    registerFirstItemCallback,
    registerOpenCartCallback,
    openCart,
  };

  return <RewardCartContext.Provider value={value}>{children}</RewardCartContext.Provider>;
};

RewardCartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};



// WEBPACK FOOTER //
// ./src/hooks/contexts/RewardCartContext.jsx